import { useLiveQuery } from 'dexie-react-hooks';
import L from 'leaflet';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { db } from '../../data/db';
import { ACTIVE_PESTS, jobsForClient, serviceClass, type ServiceClass } from '../../data/domain';
import { useClients, useJobs, usePestActivity } from '../../data/hooks';
import type { LatLng } from '../../data/types';
import { displayDate, escapeHtml, plural } from '../../lib/format';
import { currentPosition, locateAddress, locationErrorMessage, searchAddress, TOWNSVILLE } from '../../lib/geo';
import { isOnline } from '../../sync/connectivity';
import { useSyncStatus } from '../../sync/status';
import { AddressLookup } from '../../ui/Lookups';
import { Icon } from '../../ui/Icon';
import { PageHeading, View } from '../../ui/View';
import { locationMarker, markerIcon, useSatelliteMap } from './leaflet';

type Filter = 'all' | ServiceClass;
const FILTERS: Array<{ value: Filter; label: string }> = [{ value: 'all', label: 'All services' }, { value: 'general', label: 'General pest' }, { value: 'rodent', label: 'Rodent' }, { value: 'termite', label: 'Termite' }];
const HEAT_COLOURS: Record<string, string> = { Cockroaches: '#d9524d', Ants: '#d99a32', Spiders: '#8055a8', Rodents: '#4a8c62', Termites: '#d06c35', Birds: '#3683a6' };

export function ServiceMapView({ active }: { active: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const map = useSatelliteMap(containerRef, TOWNSVILLE, 13, active);
  const clients = useClients();
  const jobs = useJobs();
  const activity = usePestActivity();
  const geocodes = useLiveQuery(() => db.geocodes.toArray(), []) ?? [];
  const { connection } = useSyncStatus();
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedClient, setSelectedClient] = useState('');
  const [heat, setHeat] = useState(false);
  const [heatPest, setHeatPest] = useState('All');
  const [address, setAddress] = useState('');
  const [feedback, setFeedback] = useState('OpenStreetMap data © OpenStreetMap contributors.');
  const layers = useRef<{ sites: L.LayerGroup; heat: L.LayerGroup; search?: L.Layer; me?: L.Layer } | null>(null);

  // Client sites: saved coordinates first, then this device's geocode cache.
  const sites = useMemo(() => {
    const cache = new Map(geocodes.map((row) => [row.address, row.location]));
    return clients.flatMap((client) => {
      const location = client.location ?? cache.get(client.address.trim().toLowerCase()) ?? null;
      if (!location) return [];
      const clientJobs = jobsForClient(client, jobs).filter((job) => !job.cancelled).sort((a, b) => b.date.localeCompare(a.date));
      const latest = clientJobs[0];
      const classes = new Set(clientJobs.map(serviceClass));
      return [{ client, location, type: latest ? serviceClass(latest) : 'general' as ServiceClass, classes, history: latest ? `${plural(clientJobs.length, 'service')} · last ${displayDate(latest.date)} (${latest.serviceCode})` : 'No services recorded' }];
    });
  }, [clients, jobs, geocodes]);
  const visibleSites = useMemo(() => sites.filter((site) => filter === 'all' || site.classes.has(filter) || (!site.classes.size && filter === 'general')), [sites, filter]);

  useEffect(() => {
    if (!map) return;
    layers.current = { sites: L.layerGroup().addTo(map), heat: L.layerGroup().addTo(map) };
    return () => { layers.current = null; };
  }, [map]);

  useEffect(() => {
    const group = layers.current?.sites;
    if (!group) return;
    group.clearLayers();
    visibleSites.forEach((site) => L.marker(site.location, { icon: markerIcon(site.type), title: site.client.name }).bindPopup(`<b>${escapeHtml(site.client.name)}</b><br><span>${escapeHtml(site.client.address)}</span><br><span>${escapeHtml(site.history)}</span>`).addTo(group));
  }, [visibleSites, map]);

  useEffect(() => {
    const group = layers.current?.heat;
    if (!group) return;
    group.clearLayers();
    if (!heat) return;
    activity.filter((item) => item.status === 'Active' && item.location && (heatPest === 'All' || item.pest === heatPest)).forEach((item) => {
      const colour = HEAT_COLOURS[item.pest] || '#d06c35';
      const icon = L.divIcon({ className: 'organic-heat-icon', html: `<span style="--heat-colour:${colour}" title="${escapeHtml(item.pest)}: active"></span>`, iconSize: [110, 110], iconAnchor: [55, 55] });
      L.marker(item.location as LatLng, { icon, interactive: false }).addTo(group);
    });
  }, [heat, heatPest, activity, map]);

  // While the map is open and online, look up coordinates for clients that have none yet (rate-limited).
  const pendingLookups = useRef(new Set<string>());
  useEffect(() => {
    if (!active || connection !== 'online') return;
    const known = new Set(geocodes.map((row) => row.address));
    const missing = clients.filter((client) => !client.location && client.address && !known.has(client.address.trim().toLowerCase()) && !pendingLookups.current.has(client.id));
    let cancelled = false;
    (async () => {
      for (const client of missing) {
        if (cancelled || !isOnline()) return;
        pendingLookups.current.add(client.id);
        await locateAddress(client.address);
      }
    })();
    return () => { cancelled = true; };
  }, [active, connection, clients, geocodes]);

  function focusAll() {
    if (!map) return;
    if (visibleSites.length) map.fitBounds(visibleSites.map((site) => site.location), { padding: [36, 36], maxZoom: 17 });
    else setFeedback(sites.length ? 'No sites match this filter.' : 'Client sites appear here once their addresses have been located.');
  }

  async function moveToClient(id: string) {
    setSelectedClient(id);
    const client = clients.find((item) => item.id === id);
    if (!client || !map) return;
    const location = client.location ?? await locateAddress(client.address);
    if (location) map.setView(location, 17);
    else setFeedback(`${client.name} could not be located${isOnline() ? '' : ' while offline'}.`);
  }

  async function search(event?: FormEvent) {
    event?.preventDefault();
    const query = address.trim();
    if (!query) { setFeedback('Enter an Australian address to search.'); return; }
    if (!isOnline()) { setFeedback('Address search needs a connection. Previously viewed map areas remain available offline.'); return; }
    setFeedback('Searching OpenStreetMap…');
    try {
      const [result] = await searchAddress(query);
      if (!result) { setFeedback('No matching address found. Try a fuller address.'); return; }
      showSearchResult(result.location, result.displayName);
    } catch { setFeedback('Address search is unavailable right now. Check your connection and try again.'); }
  }

  function showSearchResult(location: LatLng, displayName: string) {
    if (!map || !layers.current) return;
    if (layers.current.search) map.removeLayer(layers.current.search);
    const marker = L.marker(location, { icon: markerIcon('search') }).bindPopup(`<b>Search result</b><br>${escapeHtml(displayName)}`).addTo(map);
    marker.openPopup();
    layers.current.search = marker;
    map.setView(location, 17);
    setFeedback(displayName);
  }

  async function useMyLocation() {
    if (!map || !layers.current) return;
    setFeedback('Requesting your location…');
    try {
      const position = await currentPosition();
      const location: LatLng = [position.coords.latitude, position.coords.longitude];
      if (layers.current.me) map.removeLayer(layers.current.me);
      layers.current.me = locationMarker(location, 'Your field location').addTo(map);
      map.setView(location, 17);
      setFeedback(`Your field location loaded. Accuracy about ${Math.round(position.coords.accuracy || 0)} m.`);
    } catch (error) { setFeedback(locationErrorMessage(error)); }
  }

  return (
    <View id="map" active={active} className="map-view">
      <PageHeading eyebrow="Service intelligence" title="Service map" subtitle="Search an address, view client activity and filter services on OpenStreetMap.">
        <button className="btn btn-secondary" id="useServiceLocation" type="button" onClick={useMyLocation}><Icon name="locate" size={16} />Use my location</button>
        <button className="btn btn-primary" id="focusMap" onClick={focusAll}>Focus all sites</button>
      </PageHeading>
      <div className="toolbar">
        <div className="segmented" id="mapFilters" role="group" aria-label="Filter by service">
          {FILTERS.map((item) => <button key={item.value} type="button" aria-pressed={filter === item.value} onClick={() => setFilter(item.value)}>{item.label}</button>)}
        </div>
        <form className="search-field search-field-button toolbar-grow" id="serviceMapSearch" onSubmit={search}>
          <Icon name="search" />
          <AddressLookup id="serviceMapAddress" placeholder="Search an address" aria-label="Search an address" value={address} onChange={setAddress} onPick={(result) => showSearchResult(result.location, result.displayName)} />
          <button className="btn btn-secondary btn-sm" type="submit">Search</button>
        </form>
      </div>
      <div className="toolbar">
        <select id="serviceMapClient" aria-label="Move to client" value={selectedClient} onChange={(event) => moveToClient(event.target.value)}>
          <option value="">Go to client…</option>
          {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
        </select>
        <label className="check check-boxed"><input type="checkbox" id="heatMapToggle" checked={heat} onChange={(event) => setHeat(event.target.checked)} /> Active pest heat map</label>
        <select id="heatPestFilter" aria-label="Filter heat map by pest" value={heatPest} disabled={!heat} onChange={(event) => setHeatPest(event.target.value)}>
          <option value="All">All active pests</option>
          {ACTIVE_PESTS.map((pest) => <option key={pest}>{pest}</option>)}
        </select>
      </div>
      <div className="map-frame">
        <div className="osm-map" id="serviceMap" ref={containerRef}></div>
        <p className="map-caption" id="mapFeedback" aria-live="polite">{feedback}</p>
      </div>
    </View>
  );
}
